import React from "react";
import { Link } from "react-router-dom";
import { ArrowRightOutlined } from "@ant-design/icons";

import "./home-page.component.styles.scss";
import {
  DefaultSection,
  Testimonials,
  Marquee,
  TrustStrip,
  ReginaStory,
  Faq,
  PullQuote,
} from "../../sections";
import { conversions } from "../../../lib/analytics";
import { SeoHead } from "../../seo";
import { useLang } from "../../../lib/i18n";
import { useReveal } from "../../../lib/motion";
import { HeroVideo } from "../../generic";

// Real photography of the studio, not the painted illustration: the hero
// now shows the room you are booking rather than an artwork of one.
const heroImage = "/heroes/home.jpg";
const heroWebp = {
  xl: "/heroes/home.webp",
  md: "/heroes/home@1280.webp",
  sm: "/heroes/home@768.webp",
};

const FAQ_SCHEMA_IT = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": "https://www.laviens.com/#faq",
  mainEntity: [
    {
      "@type": "Question",
      name: "Dove si trova Laviens?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Laviens è uno studio olistico privato a Sesto Fiorentino, in provincia di Firenze (FI, Italia), con parcheggio dedicato e ottimo collegamento in treno.",
      },
    },
    {
      "@type": "Question",
      name: "Quali massaggi offre Laviens?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Massaggio svedese, decontratturante, linfodrenante, ayurvedico, californiano, emozionale, anticellulite, più aromaterapia e skin care viso e corpo.",
      },
    },
    {
      "@type": "Question",
      name: "Come si prenota un massaggio a Laviens?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Solo su appuntamento. Prenoti online su laviens.com/booking — scegli trattamento, data e ora e paghi con carta — oppure con una telefonata o un messaggio WhatsApp al +39 349 246 8186.",
      },
    },
    {
      "@type": "Question",
      name: "Chi è Regina?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Regina è la fondatrice e terapista olistica di Laviens: oltre 15 anni di pratica, 6 tecniche di massaggio padroneggiate, formazione in aromaterapia e skin care.",
      },
    },
  ],
};

const FAQ_SCHEMA_EN = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": "https://www.laviens.com/en/#faq",
  inLanguage: "en",
  mainEntity: [
    {
      "@type": "Question",
      name: "Where is Laviens located?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Laviens is a private wellness studio in Sesto Fiorentino, just outside Florence (FI, Italy), with dedicated parking and excellent train connections.",
      },
    },
    {
      "@type": "Question",
      name: "What massages does Laviens offer?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Swedish, deep-tissue, lymphatic drainage, Ayurvedic, Californian, emotional, anti-cellulite massages, plus aromatherapy and face & body skin care.",
      },
    },
    {
      "@type": "Question",
      name: "How do I book a massage at Laviens?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "By appointment only. Book online at laviens.com/booking — pick a treatment, a real free slot, and pay by card — or call/WhatsApp +39 349 246 8186.",
      },
    },
    {
      "@type": "Question",
      name: "Who is Regina?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Regina is the founder and wellness therapist at Laviens: over 15 years of practice, six massage techniques mastered, with training in aromatherapy and skin care.",
      },
    },
  ],
};

const HomePage: React.FC = () => {
  const { lang, lp } = useLang();
  const isEn = lang === "en";
  const porteEyeRef = useReveal<HTMLSpanElement>();
  const porteTitleRef = useReveal<HTMLHeadingElement>();
  const porteGridRef = useReveal<HTMLDivElement>();
  const discoverHeadRef = useReveal<HTMLElement>();
  const discoverGridRef = useReveal<HTMLDivElement>();
  const discoverFootRef = useReveal<HTMLDivElement>();
  const invitoRef = useReveal<HTMLDivElement>();
  return (
    <div className="lvs-home">
      <SeoHead
        title={
          isEn
            ? "Laviens · Massages and Wellness Studio in Florence (Sesto Fiorentino)"
            : "Laviens · Massaggi e Studio Olistico a Firenze (Sesto Fiorentino)"
        }
        description={
          isEn
            ? "Wellness studio in Sesto Fiorentino (Florence): Swedish, deep-tissue, lymphatic drainage, Ayurvedic massages, aromatherapy and skin care. 5★ Treatwell reviews. By appointment only, with Regina. Call +39 349 246 8186."
            : "Studio olistico a Sesto Fiorentino (Firenze): massaggio svedese, decontratturante, linfodrenante, ayurvedico, aromaterapia e skin care. Recensioni 5★ su Treatwell. Solo su appuntamento, con Regina. Chiama +39 349 246 8186."
        }
        path={lp("/")}
        keywords={
          isEn
            ? "massages florence, massages sesto fiorentino, wellness studio florence, swedish massage florence, deep tissue massage florence, ayurvedic massage florence, aromatherapy, skin care florence, well-being florence"
            : "massaggi firenze, massaggi sesto fiorentino, studio olistico firenze, massaggio svedese, massaggio decontratturante, massaggio ayurvedico, aromaterapia, skin care firenze, benessere firenze"
        }
        image="/og-cover.jpg"
        breadcrumbs={[{ name: isEn ? "Home" : "Home", path: lp("/") }]}
        speakableCssSelectors={[".lvs-cover__title", ".lvs-cover__sub"]}
        jsonLd={[isEn ? FAQ_SCHEMA_EN : FAQ_SCHEMA_IT]}
      />

      {/* ── COVER — full-bleed cinematic hero: the room breathes behind the promise ── */}
      <section className="lvs-cover">
        <HeroVideo
          className="lvs-cover__media"
          mp4Src="/heroes/home-loop.mp4"
          imageUrl={heroImage}
          imageWebp={heroWebp}
          imageAlt={
            isEn
              ? "The Laviens treatment room in Sesto Fiorentino, set up for a massage"
              : "La stanza dei trattamenti Laviens a Sesto Fiorentino, pronta per un massaggio"
          }
        />
        <div className="lvs-cover__scrim" aria-hidden="true" />
        <div className="lvs-cover__inner">
          <div className="lvs-cover__type">
            <span className="eyebrow lvs-cover__eyebrow fx-rise">
              {isEn
                ? "Wellness studio — Sesto Fiorentino, Florence"
                : "Studio olistico — Sesto Fiorentino, Firenze"}
            </span>
            <h1 className="lvs-cover__title fx-rise-1">
              {isEn ? (
                <>
                  A wellness ritual, <em>designed entirely for you.</em>
                </>
              ) : (
                <>
                  Un rituale di benessere, <em>pensato interamente per te.</em>
                </>
              )}
            </h1>
            <p className="lvs-cover__sub fx-rise-2">
              {isEn
                ? "Massage, aromatherapy and skin care in Regina's private studio. One guest at a time, by appointment only."
                : "Massaggi, aromaterapia e skin care nello studio privato di Regina. Un ospite alla volta, solo su appuntamento."}
            </p>
            <div className="lvs-cover__actions fx-rise-3">
              <Link
                to={lp("/booking")}
                className="lvs-cover__cta"
                onClick={() => conversions.book("home_cover")}
              >
                {isEn ? "Book online" : "Prenota online"}
                <ArrowRightOutlined aria-hidden="true" />
              </Link>
              <Link to={lp("/treatments")} className="lvs-cover__ghost">
                {isEn ? "The treatments" : "I trattamenti"}
              </Link>
            </div>
            <p className="lvs-cover__trust fx-rise-3">
              <span className="lvs-cover__star" aria-hidden="true">★</span> 5,0{" "}
              {isEn ? "on Treatwell and Google" : "su Treatwell e Google"} ·{" "}
              {isEn ? "by" : "di"} <em>Regina</em>
            </p>
          </div>

        </div>
      </section>

      <TrustStrip />

      {/* ── LE PORTE — painted doors into the studio's three worlds ── */}
      <section className="lvs-porte" aria-labelledby="porte-title">
        <div className="lvs-porte__inner">
          <span className="eyebrow lvs-porte__eye lvs-reveal" ref={porteEyeRef}>
            {isEn ? "The studio" : "Lo studio"}
          </span>
          <h2
            id="porte-title"
            className="lvs-porte__title lvs-reveal"
            ref={porteTitleRef}
            style={{ "--lvs-d": "90ms" } as React.CSSProperties}
          >
            {isEn ? (
              <>
                Three doors, <em>one ritual.</em>
              </>
            ) : (
              <>
                Tre porte, <em>un rituale.</em>
              </>
            )}
          </h2>
          <div className="lvs-porte__grid lvs-reveal-group" ref={porteGridRef}>
            <Link
              to={lp("/treatments")}
              className="lvs-porte__card"
              onClick={() => conversions.book("home_porte", "massaggi")}
            >
              <span className="lvs-porte__frame">
                <img src="/heroes/massaggio-decontratturante@768.webp" alt="" loading="lazy" decoding="async" />
              </span>
              <span className="lvs-porte__label">{isEn ? "Massages" : "Massaggi"}</span>
              <span className="lvs-porte__hint">
                {isEn ? "Seven techniques, one listening touch" : "Sette tecniche, un tocco che ascolta"}
              </span>
            </Link>
            <Link
              to={lp("/epilazione-brasiliana")}
              className="lvs-porte__card"
              onClick={() => conversions.book("home_porte", "epilazione")}
            >
              <span className="lvs-porte__frame">
                <img src="/heroes/epilazione-cera-calda@768.webp" alt="" loading="lazy" decoding="async" />
              </span>
              <span className="lvs-porte__label">{isEn ? "Waxing" : "Epilazione"}</span>
              <span className="lvs-porte__hint">
                {isEn ? "Hot wax, Brazilian technique, full discretion" : "Cera calda, tecnica brasiliana, massima discrezione"}
              </span>
            </Link>
            <Link
              to={lp("/booking")}
              className="lvs-porte__card"
              onClick={() => conversions.book("home_porte", "rituali")}
            >
              <span className="lvs-porte__frame">
                <img src="/heroes/massaggio-ayurvedico@768.webp" alt="" loading="lazy" decoding="async" />
              </span>
              <span className="lvs-porte__label">{isEn ? "Rituals" : "Rituali"}</span>
              <span className="lvs-porte__hint">
                {isEn ? "Exfoliation and massage, one experience" : "Esfoliazione e massaggio, un'unica esperienza"}
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* ── PULL QUOTE: the manifesto, very big ── */}
      <PullQuote
        attribution={isEn ? "— Laviens Manifesto" : "— Manifesto Laviens"}
        tone="paper"
      >
        {isEn ? "The body remembers everything." : "Il corpo ricorda tutto."}
        <br />
        <em>
          {isEn ? "We teach how to let go." : "Noi insegniamo a lasciar andare."}
        </em>
      </PullQuote>

      <Marquee />

      <DefaultSection
        index="01"
        eyebrow={isEn ? "Holistic Massages" : "Massaggi Olistici"}
        imageUrl="/heroes/massaggio-decontratturante.jpg"
        imageAlt={
          isEn
            ? "Holistic deep-tissue massage session at the Laviens studio"
            : "Sessione di massaggio olistico decontratturante allo studio Laviens"
        }
        orientation="left"
        title={
          isEn
            ? "A new rhythm, down to your fingertips."
            : "Un ritmo nuovo, fino alla punta delle dita."
        }
        description={
          isEn
            ? "Massage is a natural way to improve daily performance, live pain-free, and restore overall well-being of body and mind."
            : "Il massaggio è un modo naturale per migliorare le prestazioni quotidiane, vivere senza dolore e ritrovare il benessere generale del corpo e della mente."
        }
        buttonText={isEn ? "Book a massage" : "Prenota un massaggio"}
      />
      <DefaultSection
        index="02"
        eyebrow="Skin Care"
        imageUrl="/heroes/skin-care.jpg"
        imageAlt={
          isEn
            ? "Personalized facial skin-care treatment at Laviens Wellness Studio"
            : "Trattamento skin care viso personalizzato a Laviens Studio Olistico"
        }
        orientation="right"
        title={
          isEn
            ? "Care for your skin, as a gesture of listening."
            : "Cura la tua pelle, come un gesto di ascolto."
        }
        description={
          isEn
            ? "Caring for your skin isn't just a treat: it's a necessary practice to keep it beautiful, healthy, and radiant over time."
            : "Prendersi cura della pelle non è solo una coccola: è una precauzione necessaria per mantenerla bella, sana e luminosa nel tempo."
        }
        buttonText={isEn ? "Discover treatments" : "Scopri i trattamenti"}
        to="/treatments"
      />
      <DefaultSection
        index="03"
        eyebrow={isEn ? "Aromatherapy" : "Aromaterapia"}
        imageUrl="/heroes/aromaterapia.jpg"
        imageAlt={
          isEn
            ? "Essential oils and candles for an aromatherapy treatment"
            : "Olii essenziali e candele per un trattamento di aromaterapia"
        }
        orientation="left"
        title={
          isEn
            ? "The breath that restores balance."
            : "Il respiro che riporta l'equilibrio."
        }
        description={
          isEn
            ? "Aromatherapy combines the caring touch of massage with natural essential oils. Two primal senses — touch and smell — that help find physical, mental, and emotional harmony."
            : "L'aromaterapia unisce il tocco premuroso del massaggio agli oli essenziali naturali. Due sensi primordiali — il tatto e l'olfatto — che aiutano a trovare armonia fisica, mentale ed emotiva."
        }
        buttonText={isEn ? "Try the experience" : "Provare il benessere"}
      />

      <PullQuote
        attribution={
          isEn
            ? "— Arianna · Ayurvedic Massage"
            : "— Arianna · Massaggio Ayurvedico"
        }
        tone="ink"
      >
        {isEn ? (
          <>
            She brings real <em>passion</em> and <em>dedication</em>.
          </>
        ) : (
          <>
            Ci mette <em>passione</em> e <em>dedizione</em>.
          </>
        )}
      </PullQuote>

      <ReginaStory />

      <Testimonials />


      {/* ── Internal linking: Journal + location pages ── */}
      <section className="lvs-home__discover lvs-container">
        <header className="lvs-home__discoverHead lvs-reveal" ref={discoverHeadRef}>
          <span className="eyebrow">
            {isEn ? "Read or explore" : "Leggi o esplora"}
          </span>
          <h2>
            {isEn ? "Go deeper, " : "Approfondisci, "}
            <em>{isEn ? "without rushing" : "senza fretta"}</em>.
          </h2>
        </header>

        <div className="lvs-home__discoverGrid lvs-reveal-group" ref={discoverGridRef}>
          <article className="lvs-home__discoverCard">
            <span className="eyebrow">{isEn ? "Guide" : "Guida"}</span>
            <h3>
              <Link to={lp("/journal/svedese-o-decontratturante")}>
                {isEn
                  ? "Swedish or deep-tissue? How to choose"
                  : "Svedese o decontratturante? Come scegliere"}
              </Link>
            </h3>
            <p>
              {isEn
                ? "Two different massages — and a practical guide to help you choose based on how you feel."
                : "Due massaggi diversi — e una guida pratica per capire quale prenotare in base a come stai."}
            </p>
            <Link
              to={lp("/journal/svedese-o-decontratturante")}
              className="lvs-home__discoverLink"
            >
              {isEn ? "Read" : "Leggi"} <ArrowRightOutlined />
            </Link>
          </article>

          <article className="lvs-home__discoverCard">
            <span className="eyebrow">{isEn ? "Guide" : "Guida"}</span>
            <h3>
              <Link to={lp("/journal/cose-il-linfodrenaggio")}>
                {isEn
                  ? "What lymphatic drainage is — and when it helps"
                  : "Cos'è il linfodrenaggio e quando serve"}
              </Link>
            </h3>
            <p>
              {isEn
                ? "It's not a relaxing massage and it's not anti-cellulite. The Vodder method, explained clearly."
                : "Non è un massaggio rilassante e non è un anticellulite. Il metodo Vodder spiegato bene."}
            </p>
            <Link
              to={lp("/journal/cose-il-linfodrenaggio")}
              className="lvs-home__discoverLink"
            >
              {isEn ? "Read" : "Leggi"} <ArrowRightOutlined />
            </Link>
          </article>

          <article className="lvs-home__discoverCard lvs-home__discoverCard--accent">
            <span className="eyebrow">
              {isEn ? "Where to find us" : "Dove siamo"}
            </span>
            <h3>{isEn ? "Massages in your area" : "Massaggi nella tua zona"}</h3>
            <ul className="lvs-home__discoverLinks">
              <li>
                <Link to={lp("/massaggi-firenze")}>
                  {isEn ? "Massages Florence" : "Massaggi Firenze"}{" "}
                  <ArrowRightOutlined />
                </Link>
              </li>
              <li>
                <Link to={lp("/massaggi-sesto-fiorentino")}>
                  {isEn
                    ? "Massages Sesto Fiorentino"
                    : "Massaggi Sesto Fiorentino"}{" "}
                  <ArrowRightOutlined />
                </Link>
              </li>
              <li>
                <Link to={lp("/massaggi-toscana")}>
                  {isEn ? "Massages in Tuscany" : "Massaggi in Toscana"}{" "}
                  <ArrowRightOutlined />
                </Link>
              </li>
            </ul>
          </article>
        </div>

        <div className="lvs-home__discoverFoot lvs-reveal lvs-reveal--fade" ref={discoverFootRef}>
          <Link to={lp("/journal")} className="lvs-home__discoverMore">
            {isEn ? "Read the Journal" : "Tutto il Journal"}{" "}
            <ArrowRightOutlined />
          </Link>
        </div>
      </section>

      <Faq />

      {/* ── L'INVITO — closing environment: inside the night garden ── */}
      <section className="lvs-invito" aria-labelledby="invito-title">
        <div className="lvs-invito__inner lvs-reveal-group" ref={invitoRef}>
          <span className="eyebrow lvs-invito__eye">
            {isEn ? "Your ritual awaits" : "Il tuo rituale ti aspetta"}
          </span>
          <h2 id="invito-title" className="lvs-invito__title">
            {isEn ? (
              <>
                A moment <em>just for you.</em>
              </>
            ) : (
              <>
                Un tempo <em>solo per te.</em>
              </>
            )}
          </h2>
          <p className="lvs-invito__sub">
            {isEn
              ? "Pick a real free slot, pay online, and the room is yours. One guest at a time."
              : "Scegli un orario libero reale, paga online, e la stanza è tua. Un ospite alla volta."}
          </p>
          <Link
            to={lp("/booking")}
            className="lvs-invito__cta"
            onClick={() => conversions.book("home_invito")}
          >
            {isEn ? "Book online now" : "Prenota online adesso"}
            <ArrowRightOutlined aria-hidden="true" />
          </Link>
        </div>
      </section>

      <PullQuote
        attribution={
          isEn ? "— Laviens, Sesto Fiorentino" : "— Laviens, Sesto Fiorentino"
        }
        tone="paper"
      >
        {isEn ? (
          <>
            When you're ready to <em>breathe</em> again,
            <br />
            you'll find us here.
          </>
        ) : (
          <>
            Quando vuoi tornare a <em>respirare,</em>
            <br />
            ci trovi qui.
          </>
        )}
      </PullQuote>
    </div>
  );
};

export default HomePage;
